import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { apiAction, generalSet } from '../../../store/generalSlice';
import Spinner from '../../../layout/Spinner';
import { parseJSON } from '../../../utils/helpers';
import CreatableSelect from 'react-select/creatable';
import countries from '../../../utils/ISO3166-1.alpha2.json';

const FIELD_TYPE_OPTIONS = ['USE_SOURCE', 'CUSTOM_VALUE', 'DISABLE_FIELD'];

const COUNTRY_OPTIONS = countries.map((country) => ({
  value: country.key,
  label: `${country.value} (${country.key})`,
}));

const OPTION_SETS = {
  countries: COUNTRY_OPTIONS,
  locales: [
    { value: 6, label: 'English (US) - 6' },
    { value: 24, label: 'English (UK) - 24' },
    { value: 1002, label: 'Spanish - 1002' },
    { value: 16, label: 'French - 16' },
    { value: 10, label: 'Italian - 10' },
    { value: 7, label: 'German - 7' },
    { value: 9, label: 'Dutch - 9' },
    { value: 14, label: 'Portuguese (Brazil) - 14' },
    { value: 1043, label: 'Portuguese (Portugal) - 1043' },
    { value: 11, label: 'Japanese - 11' },
    { value: 12, label: 'Korean - 12' },
    { value: 19, label: 'Chinese (Simplified) - 19' },
    { value: 20, label: 'Chinese (Traditional) - 20' },
    { value: 1031, label: 'Arabic - 1031' },
    { value: 1033, label: 'Hebrew - 1033' },
    { value: 1035, label: 'Russian - 1035' },
    { value: 17, label: 'Turkish - 17' },
    { value: 21, label: 'Indonesian - 21' },
    { value: 25, label: 'Thai - 25' },
    { value: 27, label: 'Vietnamese - 27' },
  ],
  genders: [
    { value: 1, label: '1 - Male' },
    { value: 2, label: '2 - Female' },
  ],
  relationship_statuses: [
    { value: 1, label: '1 - Single' },
    { value: 2, label: '2 - In a relationship' },
    { value: 3, label: '3 - Married' },
    { value: 4, label: '4 - Engaged' },
  ],
  location_types: [
    { value: 'home', label: 'home' },
    { value: 'recent', label: 'recent' },
    { value: 'travel_in', label: 'travel_in' },
  ],
  publisher_platforms: [
    { value: 'facebook', label: 'facebook' },
    { value: 'instagram', label: 'instagram' },
    { value: 'audience_network', label: 'audience_network' },
    { value: 'messenger', label: 'messenger' },
  ],
  facebook_positions: [
    { value: 'feed', label: 'feed' },
    { value: 'right_hand_column', label: 'right_hand_column' },
    { value: 'instant_article', label: 'instant_article' },
    { value: 'video_feeds', label: 'video_feeds' },
    { value: 'marketplace', label: 'marketplace' },
    { value: 'facebook_reels', label: 'facebook_reels' },
  ],
  instagram_positions: [
    { value: 'stream', label: 'stream' },
    { value: 'story', label: 'story' },
    { value: 'reels', label: 'reels' },
    { value: 'explore', label: 'explore' },
    { value: 'search', label: 'search' },
  ],
  audience_network_positions: [
    { value: 'classic', label: 'classic' },
    { value: 'instream_video', label: 'instream_video' },
    { value: 'rewarded_video', label: 'rewarded_video' },
  ],
  messenger_positions: [
    { value: 'messenger_home', label: 'messenger_home' },
    { value: 'sponsored_messages', label: 'sponsored_messages' },
    { value: 'story', label: 'story' },
  ],
  device_platforms: [
    { value: 'mobile', label: 'mobile' },
    { value: 'desktop', label: 'desktop' },
  ],
  user_os: [
    { value: 'iOS', label: 'iOS' },
    { value: 'Android', label: 'Android' },
    { value: 'Windows', label: 'Windows' },
    { value: 'Windows Phone', label: 'Windows Phone' },
    { value: 'macOS', label: 'macOS' },
    { value: 'Linux', label: 'Linux' },
  ],
  user_device: [
    { value: 'iPhone', label: 'iPhone' },
    { value: 'iPad', label: 'iPad' },
    { value: 'Android Smartphone', label: 'Android Smartphone' },
    { value: 'Android Tablet', label: 'Android Tablet' },
    { value: 'Windows Phone', label: 'Windows Phone' },
    { value: 'Desktop', label: 'Desktop' },
  ],
};

const TARGETING_FIELDS = [
  { path: 'age_min', inputType: 'number' },
  { path: 'age_max', inputType: 'number' },
  { path: 'genders', inputType: 'numberArray', optionSet: 'genders' },
  { path: 'locales', inputType: 'numberArray', optionSet: 'locales' },

  { path: 'geo_locations.countries', inputType: 'stringArray', optionSet: 'countries' },
  { path: 'geo_locations.country_groups', inputType: 'stringArray', optionSet: 'countries' },
  { path: 'geo_locations.regions', inputType: 'json' },
  { path: 'geo_locations.cities', inputType: 'json' },
  { path: 'geo_locations.zips', inputType: 'json' },
  { path: 'geo_locations.geo_markets', inputType: 'json' },
  { path: 'geo_locations.custom_locations', inputType: 'json' },
  { path: 'geo_locations.location_types', inputType: 'stringArray', optionSet: 'location_types' },

  { path: 'excluded_geo_locations.countries', inputType: 'stringArray', optionSet: 'countries' },
  { path: 'excluded_geo_locations.country_groups', inputType: 'stringArray', optionSet: 'countries' },
  { path: 'excluded_geo_locations.regions', inputType: 'json' },
  { path: 'excluded_geo_locations.cities', inputType: 'json' },
  { path: 'excluded_geo_locations.zips', inputType: 'json' },
  { path: 'excluded_geo_locations.geo_markets', inputType: 'json' },
  { path: 'excluded_geo_locations.custom_locations', inputType: 'json' },
  { path: 'excluded_geo_locations.location_types', inputType: 'stringArray', optionSet: 'location_types' },

  { path: 'industries', inputType: 'json' },
  { path: 'life_events', inputType: 'json' },
  { path: 'relationship_statuses', inputType: 'numberArray', optionSet: 'relationship_statuses' },
  { path: 'interests', inputType: 'json' },
  { path: 'behaviors', inputType: 'json' },

  { path: 'flexible_spec', inputType: 'json' },
  { path: 'exclusions', inputType: 'json' },

  { path: 'publisher_platforms', inputType: 'stringArray', optionSet: 'publisher_platforms' },
  { path: 'facebook_positions', inputType: 'stringArray', optionSet: 'facebook_positions' },
  { path: 'instagram_positions', inputType: 'stringArray', optionSet: 'instagram_positions' },
  { path: 'audience_network_positions', inputType: 'stringArray', optionSet: 'audience_network_positions' },
  { path: 'messenger_positions', inputType: 'stringArray', optionSet: 'messenger_positions' },
  { path: 'device_platforms', inputType: 'stringArray', optionSet: 'device_platforms' },
  { path: 'user_device', inputType: 'stringArray', optionSet: 'user_device' },
  { path: 'user_os', inputType: 'stringArray', optionSet: 'user_os' },

  { path: 'custom_audiences', inputType: 'json' },
  { path: 'excluded_custom_audiences', inputType: 'json' },
];

const splitPath = (path) => path.split('.');

const getByPath = (objectValue, path) => {
  const parts = splitPath(path);
  let current = objectValue;

  for (let index = 0; index < parts.length; index += 1) {
    if (current == null) {
      return undefined;
    }
    current = current[parts[index]];
  }

  return current;
};

const setByPath = (objectValue, path, newValue) => {
  const parts = splitPath(path);
  const result = Array.isArray(objectValue) ? [...objectValue] : { ...(objectValue || {}) };

  let current = result;

  for (let index = 0; index < parts.length - 1; index += 1) {
    const key = parts[index];
    const next = current[key];
    current[key] = Array.isArray(next) ? [...next] : { ...(next || {}) };
    current = current[key];
  }

  current[parts[parts.length - 1]] = newValue;
  return result;
};

const getArrayFromPath = (objectValue, path) => {
  const value = getByPath(objectValue, path);
  return Array.isArray(value) ? value : [];
};

const toSelectValue = (values, options) => {
  if (!Array.isArray(values)) {
    return [];
  }

  if (!Array.isArray(options) || !options.length) {
    return values.map((value) => ({ value, label: String(value) }));
  }

  return values.map((value) => {
    const matchingOption = options.find((option) => option.value === value);
    return matchingOption || { value, label: String(value) };
  });
};

const valueToText = (value, inputType) => {
  if (inputType === 'number') {
    return typeof value === 'number' ? String(value) : '';
  }

  if (inputType === 'stringArray' || inputType === 'numberArray') {
    return Array.isArray(value) ? value.join(',') : '';
  }

  if (inputType === 'json') {
    if (typeof value === 'undefined') {
      return '';
    }

    try {
      return JSON.stringify(value, null, 2);
    } catch (err) {
      return '';
    }
  }

  return typeof value === 'string' ? value : '';
};

const parseTextByType = (text, inputType) => {
  const trimmed = typeof text === 'string' ? text.trim() : '';

  if (inputType === 'number') {
    if (!trimmed.length) {
      return { value: undefined };
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) {
      return { error: 'Must be a valid number' };
    }

    return { value: parsed };
  }

  if (inputType === 'stringArray') {
    if (!trimmed.length) {
      return { value: [] };
    }

    return {
      value: trimmed
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    };
  }

  if (inputType === 'numberArray') {
    if (!trimmed.length) {
      return { value: [] };
    }

    const values = trimmed
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);

    const parsedValues = values.map((item) => Number(item));
    if (parsedValues.some((item) => Number.isNaN(item))) {
      return { error: 'Must be comma-separated numbers' };
    }

    return { value: parsedValues };
  }

  if (inputType === 'json') {
    if (!trimmed.length) {
      return { value: undefined };
    }

    try {
      return { value: JSON.parse(trimmed) };
    } catch (err) {
      return { error: 'Invalid JSON format' };
    }
  }

  return { value: text };
};

const normalizeFieldTypes = (fieldTypesArray) => {
  if (!Array.isArray(fieldTypesArray)) {
    return [];
  }

  return fieldTypesArray
    .filter((item) => item && typeof item.fieldName === 'string' && typeof item.fieldType === 'string')
    .map((item) => ({ fieldName: item.fieldName, fieldType: item.fieldType }))
    .sort((left, right) => left.fieldName.localeCompare(right.fieldName));
};

const toFieldTypeMap = (fieldTypesArray) => {
  const map = {};

  if (!Array.isArray(fieldTypesArray)) {
    return map;
  }

  fieldTypesArray.forEach((item) => {
    if (item?.fieldName && item?.fieldType) {
      map[item.fieldName] = item.fieldType;
    }
  });

  return map;
};

const EditAllTargetingFields = () => {
  const dispatch = useDispatch();
  const { action_id } = useParams();
  const { action } = useSelector((state) => state.general);

  const [formStateActionData, setFormStateActionData] = useState({});
  const [targetingObjectState, setTargetingObjectState] = useState({});
  const [fieldTypeMap, setFieldTypeMap] = useState({});
  const [rawValues, setRawValues] = useState({});
  const [errorsByField, setErrorsByField] = useState({});
  const [initialTargetingObject, setInitialTargetingObject] = useState({});
  const [initialFieldTypes, setInitialFieldTypes] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const initializedActionIdRef = useRef(null);

  useEffect(() => {
    dispatch(
      apiAction({
        reduxField: 'action',
        route: 'actions/getactionById',
        actionId: action_id,
      }),
    );
  }, [dispatch, action_id]);

  const checkState = Boolean(action && action._id && action.actionData);

  useEffect(() => {
    if (!checkState) {
      return;
    }

    if (initializedActionIdRef.current === action._id) {
      return;
    }

    const targetingObject = action.targetingObject || {};
    const targetingFieldTypes = normalizeFieldTypes(action.targetingObjectFieldsType || []);

    setFormStateActionData(parseJSON(action.actionData));
    setTargetingObjectState(targetingObject);
    setFieldTypeMap(toFieldTypeMap(targetingFieldTypes));
    setInitialTargetingObject(targetingObject);
    setInitialFieldTypes(targetingFieldTypes);

    const nextRawValues = {};
    TARGETING_FIELDS.forEach((field) => {
      nextRawValues[field.path] = valueToText(getByPath(targetingObject, field.path), field.inputType);
    });
    setRawValues(nextRawValues);
    setErrorsByField({});
    initializedActionIdRef.current = action._id;
  }, [checkState, action]);

  if (action?._id !== action_id) {
    dispatch(
      generalSet({
        reduxField: 'action',
        value: null,
      }),
    );
  }

  const normalizedCurrentFieldTypes = useMemo(() => {
    return normalizeFieldTypes(
      Object.entries(fieldTypeMap).map(([fieldName, fieldType]) => ({
        fieldName,
        fieldType,
      })),
    );
  }, [fieldTypeMap]);

  const hasChanges = useMemo(() => {
    const targetingChanged =
      JSON.stringify(initialTargetingObject || {}) !== JSON.stringify(targetingObjectState || {});
    const fieldTypesChanged = JSON.stringify(initialFieldTypes) !== JSON.stringify(normalizedCurrentFieldTypes);

    return targetingChanged || fieldTypesChanged;
  }, [initialTargetingObject, targetingObjectState, initialFieldTypes, normalizedCurrentFieldTypes]);

  const hasValidationErrors = useMemo(() => {
    return Object.values(errorsByField).some((message) => Boolean(message));
  }, [errorsByField]);

  const handleFieldValueChange = (path, inputType, textValue) => {
    setRawValues((prevState) => ({ ...prevState, [path]: textValue }));

    const parsed = parseTextByType(textValue, inputType);

    if (parsed.error) {
      setErrorsByField((prevState) => ({ ...prevState, [path]: parsed.error }));
      return;
    }

    setErrorsByField((prevState) => ({ ...prevState, [path]: null }));
    setTargetingObjectState((prevState) => setByPath(prevState, path, parsed.value));
  };

  const handleFieldTypeChange = (path, fieldType) => {
    setFieldTypeMap((prevState) => ({
      ...prevState,
      [path]: fieldType,
    }));
  };

  const handleArraySelectChange = (field, selectedOptions) => {
    const selectedValues = Array.isArray(selectedOptions)
      ? selectedOptions.map((option) => (field.inputType === 'numberArray' ? Number(option.value) : option.value))
      : [];

    setErrorsByField((prevState) => ({ ...prevState, [field.path]: null }));
    setRawValues((prevState) => ({ ...prevState, [field.path]: selectedValues.join(',') }));
    setTargetingObjectState((prevState) => setByPath(prevState, field.path, selectedValues));
  };

  const handleSave = async () => {
    const fieldTypesPayload = normalizeFieldTypes(
      Object.entries(fieldTypeMap).map(([fieldName, fieldType]) => ({
        fieldName,
        fieldType,
      })),
    );

    setIsSaving(true);
    setInitialTargetingObject(targetingObjectState);
    setInitialFieldTypes(fieldTypesPayload);

    await dispatch(
      apiAction({
        reduxField: 'action',
        route: 'actions/updateDuplicateAction',
        actionId: action._id,
        fieldName: 'targetingObject',
        changes: targetingObjectState,
      }),
    );

    await dispatch(
      apiAction({
        reduxField: 'action',
        route: 'actions/updateDuplicateAction',
        actionId: action._id,
        fieldName: 'targetingObjectFieldsType',
        changes: fieldTypesPayload,
      }),
    );

    await dispatch(
      apiAction({
        reduxField: 'action',
        route: 'actions/getactionById',
        actionId: action._id,
      }),
    );

    setIsSaving(false);
  };

  if (!action?._id) {
    return <Spinner />;
  }

  console.log(action);
  return checkState ? (
    <>
      <div>
        <Link className='btn btn-outline-dark' to={`/DuplicateCampaignAction/${action_id}`}>
          Back to Action <span className='text-primary'>{formStateActionData.name}</span>
        </Link>
      </div>
      <div className='container mt-4' data-aos='fade-up' data-aos-delay='200'>
        <div className='row d-flex justify-content-center'>
          <h3>Editing Targeting Object Fields</h3>
        </div>

        <div className='row d-flex justify-content-center mb-3'>
          <div className='col-md-12'>
            <button
              className='btn btn-success'
              disabled={!hasChanges || hasValidationErrors || isSaving}
              onClick={handleSave}
            >
              Save Targeting Changes
            </button>
          </div>
        </div>

        <div className='row d-flex justify-content-center'>
          {TARGETING_FIELDS.map((field) => {
            const currentType = fieldTypeMap[field.path] || 'USE_SOURCE';
            const isDisabled = currentType !== 'CUSTOM_VALUE';
            const isJson = field.inputType === 'json';
            const isArrayField = field.inputType === 'stringArray' || field.inputType === 'numberArray';
            const fieldOptions = field.optionSet ? OPTION_SETS[field.optionSet] || [] : [];
            const error = errorsByField[field.path];

            return (
              <div key={field.path} className='col-md-6 p-3 border'>
                <div>
                  <label>
                    <b>{field.path}</b>
                  </label>
                </div>

                <div className='mb-2'>
                  <label className='mr-2'>Field Type</label>
                  <select
                    className='form-control'
                    value={currentType}
                    onChange={(event) => handleFieldTypeChange(field.path, event.target.value)}
                  >
                    {FIELD_TYPE_OPTIONS.map((typeOption) => (
                      <option key={typeOption} value={typeOption}>
                        {typeOption}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label>Value</label>

                  {isJson ? (
                    <textarea
                      className='form-control'
                      rows={5}
                      value={rawValues[field.path] || ''}
                      disabled={isDisabled}
                      onChange={(event) => handleFieldValueChange(field.path, field.inputType, event.target.value)}
                      placeholder='JSON value'
                    />
                  ) : isArrayField ? (
                    fieldOptions.length ? (
                      <CreatableSelect
                        isMulti
                        isSearchable
                        className='basic-multi-select'
                        classNamePrefix='select'
                        value={toSelectValue(getArrayFromPath(targetingObjectState, field.path), fieldOptions)}
                        options={fieldOptions}
                        isDisabled={isDisabled}
                        onChange={(selectedOptions) => handleArraySelectChange(field, selectedOptions)}
                        placeholder='Select values or type to add custom'
                      />
                    ) : (
                      <CreatableSelect
                        isMulti
                        isSearchable
                        className='basic-multi-select'
                        classNamePrefix='select'
                        value={toSelectValue(getArrayFromPath(targetingObjectState, field.path), [])}
                        isDisabled={isDisabled}
                        onChange={(selectedOptions) => handleArraySelectChange(field, selectedOptions)}
                        placeholder='Type and press enter to add values'
                      />
                    )
                  ) : (
                    <input
                      className='form-control'
                      type={field.inputType === 'number' ? 'number' : 'text'}
                      value={rawValues[field.path] || ''}
                      disabled={isDisabled}
                      onChange={(event) => handleFieldValueChange(field.path, field.inputType, event.target.value)}
                      placeholder={
                        field.inputType === 'stringArray' || field.inputType === 'numberArray'
                          ? 'Comma-separated values'
                          : ''
                      }
                    />
                  )}

                  {error ? <small className='text-danger'>{error}</small> : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  ) : (
    <Spinner />
  );
};

export default EditAllTargetingFields;
